import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { ShopHeader } from "@/components/ShopHeader";
import { StoreNav } from "@/components/StoreNav";
import { ArrowLeft, Package, FilePdf, Plus, Minus, ShoppingCart } from "@phosphor-icons/react";
import { isDeadPdf, pdfLabel, pdfOriginal, stripDeadLinks } from "@/lib/pdfUtil";
import MANUAL_FICHES from "@/data/manualFiches.json";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductPage() {
  const { productId } = useParams();
  const { t, lang } = useLang();
  const { add } = useCart();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [sel, setSel] = useState(null);

  useEffect(() => {
    setSel(null);
    axios.get(`${API}/shop/products/${productId}`).then((r) => setP(r.data)).catch(() => setP(false));
  }, [productId]);

  if (p === null) return (<div className="min-h-screen bg-background"><ShopHeader /><div className="p-10 text-muted-foreground">{t.loading}</div></div>);
  if (p === false) return (<div className="min-h-screen bg-background"><ShopHeader /><div className="p-10 text-muted-foreground">{t.noProducts}</div></div>);

  const name = p.name[lang] || p.name.fr;
  const variants = p.variants || [];
  const cur = variants.find((v) => v.product_id === (sel || p.product_id)) || { product_id: p.product_id, product_code: p.product_code, name: p.name, image: p.image, price: p.price, pdfs_fr: p.pdfs_fr, pdfs_en: p.pdfs_en };
  const curName = (cur.name && (cur.name[lang] || cur.name.fr)) || name;
  const curImage = cur.image || p.image;

  // Fiches: agrégées sur TOUTES les variations (base + variantes), avec nom éditable FR/EN,
  // dédupliquées par nom de fichier. Les fiches curées (json) ne s'affichent que si absentes du back-office.
  const pathKey = (u) => { try { return decodeURIComponent(pdfOriginal(u).split("?")[0].split("/").pop()); } catch { return u; } };
  const langList = (src) => (((lang === "en" && src?.pdfs_en?.length) ? src.pdfs_en : src?.pdfs_fr) || []);
  const byKey = new Map();
  for (const src of [p, ...variants]) {
    for (const e of langList(src)) {
      const url = typeof e === "string" ? e : (e?.url || "");
      if (!url || isDeadPdf(url)) continue;
      const key = pathKey(url);
      if (byKey.has(key)) continue;
      const nm = typeof e === "object" ? (lang === "en" ? (e.name_en || e.name_fr) : (e.name_fr || e.name_en)) : "";
      byKey.set(key, { url, label: nm });
    }
  }
  const manualEntries = [p, ...variants].map((s) => MANUAL_FICHES[s.product_id]).filter(Boolean);
  for (const manual of manualEntries) {
    const arr = (lang === "en" ? (manual.en?.length ? manual.en : manual.fr) : (manual.fr?.length ? manual.fr : manual.en)) || [];
    for (const x of arr) {
      const url = `${API}/files/${x.path}`;
      const key = pathKey(url);
      if (!byKey.has(key)) byKey.set(key, { url, label: x.label });
    }
  }
  const sheets = [...byKey.values()].map((e, i) => ({ url: e.url, label: e.label || pdfLabel(e.url, i, lang) }));

  return (
    <div className="min-h-screen bg-background">
      <ShopHeader />
      <div className="mx-auto max-w-[1280px] px-4 py-4"><StoreNav active="catalog" /></div>
      <main className="px-6 py-8">
        <button data-testid="back-to-shop" onClick={() => navigate(-1)}
          className="mb-6 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors duration-200">
          <ArrowLeft size={16} weight="bold" /> {t.backToShop}
        </button>

        <div className="grid grid-cols-1 gap-px bg-border border border-border lg:grid-cols-2">
          <div className="flex items-center justify-center bg-card p-8">
            {curImage ? (
              <img loading="lazy" decoding="async" src={curImage} alt={curName} className="max-h-[440px] w-full object-contain"
                onError={(e) => { e.currentTarget.style.display = "none"; }} />
            ) : (
              <div className="flex h-80 w-full items-center justify-center text-muted-foreground"><Package size={64} /></div>
            )}
          </div>

          <div className="bg-card p-8">
            <div className="font-mono text-sm text-primary" data-testid="product-code">{cur.product_code}{cur.value ? <span className="ml-2 text-foreground/60">· {cur.value}</span> : null}</div>
            <h1 className="mt-2 font-display text-3xl font-black tracking-tighter" data-testid="product-name">{curName}</h1>

            {variants.length > 1 && (
              <div className="mt-4">
                <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">{(lang === "en" ? (p.variation_label_en || p.variation_label) : p.variation_label) || t.chooseFormat}</label>
                <select data-testid="variant-select" value={sel || p.product_id} onChange={(e) => setSel(e.target.value)}
                  className="w-full border border-border bg-background px-3 py-2.5 text-sm outline-none focus:border-primary">
                  {variants.map((v) => <option key={v.product_id} value={v.product_id}>{v.value || (v.name && (v.name[lang] || v.name.fr))} — {v.product_code}</option>)}
                </select>
              </div>
            )}

            <div className="mt-4 flex items-center gap-3">
              <div className="flex items-center border border-border">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-2 hover:bg-muted"><Minus size={14} weight="bold" /></button>
                <span className="w-12 text-center text-sm font-semibold" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty(qty + 1)} className="px-3 py-2 hover:bg-muted"><Plus size={14} weight="bold" /></button>
              </div>
              <button data-testid="add-to-cart-detail"
                onClick={() => { add({ ...p, product_id: cur.product_id, product_code: cur.product_code, name: cur.name || p.name, image: curImage }, qty); toast.success(t.addedToCart); }}
                className="flex flex-1 items-center justify-center gap-2 bg-primary py-3 text-sm font-bold uppercase tracking-wider text-white transition-transform duration-200 ease-out hover:-translate-y-0.5">
                <ShoppingCart size={18} weight="bold" /> {t.addToCart}
              </button>
            </div>

            {sheets.length > 0 && (
              <div className="mt-6">
                <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">{t.datasheets}</div>
                <div className="space-y-2" data-testid="product-pdfs">
                  {sheets.map((s, i) => (
                    <a key={`${s.url}-${i}`} href={s.url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 border border-border px-3 py-2 text-sm hover:border-primary hover:text-primary transition-colors duration-200"
                      data-testid={`product-pdf-${i}`}>
                      <FilePdf size={18} weight="bold" className="text-primary shrink-0" />
                      <span className="truncate">{s.label}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {p.description?.[lang] || p.description?.fr ? (
          <section className="mt-6 border border-border bg-card p-8">
            <div className="mb-4 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">{t.description}</div>
            <div className="prose prose-sm max-w-none text-sm leading-relaxed [&_a]:text-primary [&_img]:my-4"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(stripDeadLinks(p.description[lang] || p.description.fr || "")) }} />
          </section>
        ) : null}

        {(p.similar || []).length > 0 && (
          <section className="mt-6 border border-border bg-card p-8" data-testid="similar-products">
            <div className="mb-4 font-display text-xl font-black tracking-tight text-foreground">{t.similarProducts}</div>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4">
              {p.similar.map((s) => (
                <a key={s.product_id} data-testid={`similar-${s.product_id}`} href={`/product/${s.product_id}`} target="_blank" rel="noopener noreferrer"
                  className="group flex h-full flex-col rounded-lg border border-border bg-card p-4 text-left transition-shadow duration-200 hover:shadow-md">
                  <div className="mb-3 flex aspect-square items-center justify-center overflow-hidden rounded-md bg-white">
                    {s.image ? <img src={s.image} alt="" loading="lazy" className="h-full w-full object-contain transition-transform duration-200 group-hover:scale-105" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      : <Package size={32} className="text-muted-foreground" />}
                  </div>
                  <div className="font-mono text-[11px] text-primary">{s.product_code}</div>
                  <div className="mt-1 line-clamp-2 text-sm font-medium leading-tight text-foreground group-hover:text-primary">{s.name?.[lang] || s.name?.fr}</div>
                </a>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
